import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useDemo } from '../../contexts/DemoContext';
import { useDebts } from '../../hooks/useDebts';
import { useTour } from '../../contexts/TourContext';
import VideoModal from '../../components/ui/VideoModal';
import { getVideoForPage } from '../../config/videoConfig';
import DemoModeBanner from '../../components/demo/DemoModeBanner';
import DebtModal from './components/DebtModal';
import DebtPaydownChart from './components/DebtPaydownChart';
import SpecialDebtEventsSection from './components/SpecialDebtEventsSection';

const DebtLiabilities = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  
  // Use the debts hook for Firebase integration
  const { 
    debts: loans, 
    loading, 
    error, 
    createDebt, 
    updateDebt, 
    deleteDebt, 
    calculateSummary 
  } = useDebts();
  const { canWrite } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;
  const { startDebtLiabilitiesTour, autoStartTour } = useTour();
  const videoConfig = getVideoForPage('debt-liabilities');

  // Auto-start tour for first-time visitors
  useEffect(() => {
    if (!loading && loans) {
      autoStartTour('debt-liabilities', startDebtLiabilitiesTour);
    }
  }, [loading, loans, autoStartTour, startDebtLiabilitiesTour]);

  const debtCategories = [
    { name: 'All', count: loans.length },
    { name: 'Home Loans', count: loans.filter(l => l.type === 'Home Loans').length },
    { name: 'Car/Vehicle Loans', count: loans.filter(l => l.type === 'Car/Vehicle Loans').length },
    { name: 'Personal Loans', count: loans.filter(l => l.type === 'Personal Loans').length },
    { name: 'Education Loans', count: loans.filter(l => l.type === 'Education Loans').length },
    { name: 'Credit Cards', count: loans.filter(l => l.type === 'Credit Cards').length },
    { name: 'Business Loans', count: loans.filter(l => l.type === 'Business Loans').length }
  ];

  // Calculate summary using the hook
  const summary = calculateSummary();
  const { totalOutstanding, totalMonthlyEMI, avgInterestRate, highInterestDebt } = summary;
  
  // Mock income for debt-to-income calculation
  const monthlyIncome = 150000;
  const debtToIncomeRatio = (totalMonthlyEMI / monthlyIncome) * 100;

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  const formatLargeCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    }
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(0)}L`;
    }
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  const getRiskLevel = (ratio) => {
    if (ratio > 50) return { level: 'High', color: 'text-red-600', bgColor: 'bg-red-100' };
    if (ratio > 40) return { level: 'Medium', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
    return { level: 'Low', color: 'text-green-600', bgColor: 'bg-green-100' };
  };

  const debtRiskLevel = getRiskLevel(debtToIncomeRatio);

  const handleAddLoan = async (loanData) => {
    try {
      await createDebt(loanData);
    } catch (error) {
      console.error('Error adding loan:', error);
      // You might want to show a toast notification here
    }
  };

  const handleEditLoan = async (loanData) => {
    try {
      console.log('handleEditLoan - Loan data:', loanData);
      console.log('handleEditLoan - Loan ID:', loanData.id);
      await updateDebt(loanData.id, loanData);
      setEditingLoan(null);
    } catch (error) {
      console.error('Error updating loan:', error);
      // You might want to show a toast notification here
    }
  };

  const handleDeleteLoan = async (loanId) => {
    try {
      console.log('Attempting to delete loan with ID:', loanId);
      const result = await deleteDebt(loanId);
      console.log('Delete result:', result);
      
      if (result === true) {
        console.log('Loan deleted successfully');
        // Optionally show a success message
      } else {
        console.error('Failed to delete loan: Unexpected result');
        // Optionally show an error message
      }
    } catch (error) {
      console.error('Error deleting loan:', error);
      // Optionally show an error message
    }
  };

  const handleSaveLoan = async (loanData) => {
    if (editingLoan) {
      await handleEditLoan(loanData);
    } else {
      await handleAddLoan(loanData);
    }
  };

  // Filter loans based on active category
  const filteredLoans = activeCategory === 'All' 
    ? loans 
    : loans.filter(loan => loan.type === activeCategory);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading debt information...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <Icon name="AlertCircle" size={48} className="mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Data</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isExpanded={sidebarExpanded} 
        onToggle={() => setSidebarExpanded(!sidebarExpanded)} 
      />
      
      <Header 
        sidebarExpanded={sidebarExpanded}
        onSidebarToggle={() => setSidebarExpanded(!sidebarExpanded)}
      />

      <DemoModeBanner />

      <main 
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-6
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="p-4 sm:p-6">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8" data-tour="debt-header">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Debt Management</h1>
                <p className="text-muted-foreground">
                  Track loans, liabilities, and optimize your debt paydown strategy.
                </p>
              </div>
              <div className="flex gap-3">
                {/* Import Statements and Prepayment Calculator - temporarily disabled
                <Button variant="outline" iconName="Download" iconPosition="left">
                  Import Statements
                </Button>
                <Button variant="outline" iconName="Calculator" iconPosition="left">
                  Prepayment Calculator
                </Button>
                */}
                <Button variant="outline" iconName="Video" iconPosition="left" onClick={() => setIsVideoOpen(true)} className="hidden sm:inline-flex">
                  Learn
                </Button>
                <Button variant="outline" iconName="HelpCircle" iconPosition="left" onClick={startDebtLiabilitiesTour} className="hidden sm:inline-flex">
                  Tour
                </Button>
                <Button iconName="Plus" iconPosition="left" onClick={() => setIsAddModalOpen(true)} disabled={!effectiveCanWrite}>
                  Add Data
                </Button>
              </div>
            </div>
          </div>

          {/* Key Metrics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-tour="debt-summary">
            {/* Total Outstanding Debt */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Home" size={20} className="text-primary" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Total Outstanding Debt</p>
                <p className="text-2xl font-bold text-foreground">{formatLargeCurrency(totalOutstanding)}</p>
                <p className="text-sm text-muted-foreground">{loans.length} active loans</p>
              </div>
            </div>

            {/* Monthly EMI Burden */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={20} className="text-accent" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Monthly EMI Burden</p>
                <p className="text-2xl font-bold text-foreground">{formatCurrency(totalMonthlyEMI)}</p>
                <p className={`text-sm font-medium ${debtRiskLevel.color}`}>
                  {debtToIncomeRatio.toFixed(1)}% of income
                </p>
              </div>
            </div>

            {/* Debt-Free Timeline */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-warning" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Debt-Free Timeline</p>
                <p className="text-2xl font-bold text-foreground">15.0 years</p>
                <p className="text-sm text-muted-foreground">Aug 2040</p>
              </div>
            </div>

            {/* Average Interest Rate */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Percent" size={20} className="text-success" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Average Interest Rate</p>
                <p className="text-2xl font-bold text-foreground">{avgInterestRate.toFixed(1)}%</p>
                <p className="text-sm text-muted-foreground">
                  {formatCurrency(highInterestDebt)} high interest debt
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="xl:col-span-3 space-y-8">
              {/* Loan Categories */}
              <div className="bg-card rounded-lg border border-border p-6" data-tour="debt-table">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="List" size={16} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Loan Categories</h2>
                    <p className="text-sm text-muted-foreground">{filteredLoans.length} loans in all categories</p>
                  </div>
                </div>

                {/* Debt Category Tabs */}
                <div className="flex flex-wrap gap-2 mb-6" data-tour="debt-categories">
                  {debtCategories.map(category => {
                    const isActive = activeCategory === category.name;
                    const categoryLoans = category.name === 'All' 
                      ? loans 
                      : loans.filter(loan => loan.type === category.name);
                    
                    return (
                      <button
                        key={category.name}
                        onClick={() => setActiveCategory(category.name)}
                        className={`
                          flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                          ${isActive 
                            ? 'bg-primary text-primary-foreground shadow-soft' 
                            : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                          }
                        `}
                      >
                        <Icon 
                          name={
                            category.name === 'All' ? 'Grid3X3' :
                            category.name === 'Home Loans' ? 'Home' :
                            category.name === 'Car/Vehicle Loans' ? 'Car' :
                            category.name === 'Personal Loans' ? 'User' :
                            category.name === 'Education Loans' ? 'GraduationCap' :
                            category.name === 'Credit Cards' ? 'CreditCard' :
                            'Briefcase'
                          } 
                          size={16} 
                        />
                        {category.name}
                        <span className={`
                          px-2 py-0.5 rounded-full text-xs
                          ${isActive 
                            ? 'bg-primary-foreground/20 text-primary-foreground' 
                            : 'bg-background text-muted-foreground'
                          }
                        `}>
                          {category.count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Loan Table */}
                {filteredLoans.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            <div className="flex items-center gap-2">
                              LOAN NAME
                              <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />
                            </div>
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            <div className="flex items-center gap-2">
                              TYPE
                              <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />
                            </div>
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            <div className="flex items-center gap-2">
                              OUTSTANDING AMOUNT
                              <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
                            </div>
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            <div className="flex items-center gap-2">
                              START DATE
                              <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />
                            </div>
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            INTEREST RATE
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            ACTIONS
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLoans.map(loan => (
                          <tr key={loan.id} className="border-b border-border/50 hover:bg-muted/30">
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-3">
                                <Icon 
                                  name={
                                    loan.type === 'Home Loans' ? 'Home' :
                                    loan.type === 'Car/Vehicle Loans' ? 'Car' :
                                    loan.type === 'Personal Loans' ? 'User' :
                                    loan.type === 'Education Loans' ? 'GraduationCap' :
                                    loan.type === 'Credit Cards' ? 'CreditCard' :
                                    'Briefcase'
                                  } 
                                  size={16} 
                                  className="text-muted-foreground"
                                />
                                <div>
                                  <div className="font-medium text-foreground">{loan.name}</div>
                                  <div className="text-sm text-muted-foreground">{loan.bank}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <span className="text-sm text-foreground">{loan.type}</span>
                            </td>
                            <td className="py-4 px-4">
                              <div>
                                <div className="font-medium text-foreground">{formatLargeCurrency(loan.outstanding)}</div>
                                <div className="text-sm text-muted-foreground">EMI: {formatCurrency(loan.monthlyEMI)}</div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <span className="text-sm text-muted-foreground">
                                {new Date(loan.startDate).toLocaleDateString()}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <div>
                                <div className="font-medium text-foreground">{loan.interestRate}%</div>
                                <div className="text-sm text-muted-foreground">({loan.tenure} years)</div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingLoan(loan);
                                    setIsAddModalOpen(true);
                                  }}
                                  className="h-8 w-8 p-0"
                                >
                                  <Icon name="Edit2" size={14} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to delete "${loan.name}"?`)) {
                                      handleDeleteLoan(loan.id);
                                    }
                                  }}
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                >
                                  <Icon name="Trash2" size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {activeCategory === 'All' ? 'No loans added yet' : `No ${activeCategory} found`}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {activeCategory === 'All' 
                        ? 'Start by adding your first loan or debt' 
                        : `Add your first ${activeCategory.toLowerCase()} to get started`
                      }
                    </p>
                    <Button onClick={() => setIsAddModalOpen(true)}>
                      Add {activeCategory === 'All' ? 'Loan' : activeCategory.slice(0, -1)}
                    </Button>
                  </div>
                )}
              </div>

              {/* Special Outflow Events first */}
              <div data-tour="debt-events">
                <SpecialDebtEventsSection />
              </div>

              {/* Debt Paydown Timeline */}
              <div className="bg-card rounded-lg border border-border p-6" data-tour="debt-chart">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="TrendingDown" size={16} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Debt Paydown Timeline</h2>
                      <p className="text-sm text-muted-foreground">
                        Track your journey to debt freedom over time
                      </p>
                    </div>
                  </div>
                </div>

                <DebtPaydownChart loans={loans} />
              </div>

              {/* Debt Overview moved below Special Outflow Events */}
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="PieChart" size={16} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Debt Overview</h3>
                    <p className="text-sm text-muted-foreground">Summary of your liabilities</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Debt</span>
                    <span className="font-medium text-foreground">{formatLargeCurrency(totalOutstanding)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Monthly EMI</span>
                    <span className="font-medium text-foreground">{formatCurrency(totalMonthlyEMI)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Avg Interest Rate</span>
                    <span className="font-medium text-foreground">{avgInterestRate.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Active Loans</span>
                    <span className="font-medium text-foreground">{loans.length}</span>
                  </div>
                </div>
              </div>

              {/* Debt Risk Analysis - temporarily disabled */}

              {/* Optimization Strategies - temporarily disabled */}
            </div>

            {/* Right Column - Sidebar removed as Overview moved below */}
          </div>
        </div>
      </main>

      {/* Debt Modal */}
      <DebtModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingLoan(null);
        }}
        onSave={handleSaveLoan}
        editingLoan={editingLoan}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl={videoConfig?.url}
        title={videoConfig?.title}
      />
    </div>
  );
};

export default DebtLiabilities; 